import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import DashboardLayout from "../../components/DashboardLayout";
import { Building2, Search, Save, CheckCircle, AlertCircle, Building, Phone, Mail, MapPin, User, FileText } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientProfile() {
  const { t } = useTranslation();
  const token = localStorage.getItem("scaffoldx_token");
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lookupOrgNumber, setLookupOrgNumber] = useState("");
  const [lookingUp, setLookingUp] = useState(false);
  
  // Fetch profile on mount
  useEffect(() => {
    fetchProfile();
  }, []);
  
  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API_URL}/api/client-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
      } else {
        toast.error("Kunne ikke hente firmaprofil");
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Feil ved henting av profil");
    } finally {
      setLoading(false);
    }
  };
  
  const handleLookup = async () => {
    if (!lookupOrgNumber || lookupOrgNumber.length < 9) {
      toast.error("Vennligst skriv inn et gyldig org.nummer (9 siffer)");
      return;
    }
    
    setLookingUp(true);
    try {
      const res = await fetch(`${API_URL}/api/client-profile/from-lookup/${lookupOrgNumber}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setProfile(data.profile);
        toast.success("Firmainformasjon hentet fra Brønnøysundregisteret!");
      } else {
        const error = await res.json();
        toast.error(error.detail || "Kunne ikke finne firma");
      }
    } catch (error) {
      console.error("Lookup error:", error);
      toast.error("Feil ved oppslag");
    } finally {
      setLookingUp(false);
    }
  };
  
  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/client-profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(profile)
      });
      
      if (res.ok) {
        const data = await res.json();
        setProfile(data.profile);
        toast.success("Profil oppdatert!");
      } else {
        toast.error("Kunne ikke oppdatere profil");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Feil ved lagring");
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#23F7DD]"></div>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Building2 className="w-6 h-6 text-[#23F7DD]" />
              Firmaprofil
            </h1>
            <p className="text-gray-400 mt-1">
              Administrer firmaopplysningene dine for kontrakter og fakturaer
            </p>
          </div>
          
          {profile?.is_complete ? (
            <div className="flex items-center gap-2 text-green-400 bg-green-400/10 px-4 py-2 rounded-lg">
              <CheckCircle className="w-5 h-5" />
              <span>Komplett profil</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-yellow-400 bg-yellow-400/10 px-4 py-2 rounded-lg">
              <AlertCircle className="w-5 h-5" />
              <span>Ufullstendig profil</span>
            </div>
          )}
        </div>
        
        {/* Lookup Card */}
        <Card className="bg-[#1E293B] border-[#334155]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Search className="w-5 h-5 text-[#23F7DD]" />
              Hent fra Brønnøysundregisteret
            </CardTitle>
            <CardDescription>
              Skriv inn organisasjonsnummer for å hente firmainformasjon automatisk
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Input
                placeholder="Org.nummer (9 siffer)"
                value={lookupOrgNumber}
                onChange={(e) => setLookupOrgNumber(e.target.value.replace(/\D/g, "").slice(0, 9))}
                className="bg-[#0F172A] border-[#334155] text-white max-w-xs"
              />
              <Button 
                onClick={handleLookup}
                disabled={lookingUp || lookupOrgNumber.length < 9}
                className="bg-[#23F7DD] text-black hover:bg-[#1ACBB8]"
              >
                {lookingUp ? "Søker..." : "Hent info"}
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Profile Form */}
        <Card className="bg-[#1E293B] border-[#334155]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Building className="w-5 h-5 text-[#23F7DD]" />
              Firmainformasjon
            </CardTitle>
            <CardDescription>
              Denne informasjonen brukes i kontrakter og på fakturaer
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Company Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Firmanavn *</Label>
                <Input
                  value={profile?.company_name || ""}
                  onChange={(e) => handleChange("company_name", e.target.value)}
                  placeholder="Ditt firma AS"
                  className="bg-[#0F172A] border-[#334155] text-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Org.nummer *</Label>
                <Input
                  value={profile?.org_number || ""}
                  onChange={(e) => handleChange("org_number", e.target.value.replace(/\D/g, "").slice(0, 9))}
                  placeholder="123456789"
                  className="bg-[#0F172A] border-[#334155] text-white"
                />
              </div>
            </div>
            
            {/* Address */}
            <div className="space-y-4">
              <h4 className="text-white font-medium flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#23F7DD]" />
                Adresse
              </h4>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Gateadresse *</Label>
                <Input
                  value={profile?.address || ""}
                  onChange={(e) => handleChange("address", e.target.value)}
                  placeholder="Gatenavn 123"
                  className="bg-[#0F172A] border-[#334155] text-white"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Postnummer</Label>
                  <Input
                    value={profile?.postal_code || ""}
                    onChange={(e) => handleChange("postal_code", e.target.value.replace(/\D/g, "").slice(0, 4))}
                    placeholder="0001"
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-300">By *</Label>
                  <Input
                    value={profile?.city || ""}
                    onChange={(e) => handleChange("city", e.target.value)}
                    placeholder="Oslo"
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-300">Land</Label>
                  <Input
                    value={profile?.country || "Norway"}
                    onChange={(e) => handleChange("country", e.target.value)}
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </div>
              </div>
            </div>
            
            {/* Contact */}
            <div className="space-y-4">
              <h4 className="text-white font-medium flex items-center gap-2">
                <User className="w-4 h-4 text-[#23F7DD]" />
                Kontaktinformasjon
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Kontaktperson *</Label>
                  <Input
                    value={profile?.contact_person || ""}
                    onChange={(e) => handleChange("contact_person", e.target.value)}
                    placeholder="Navn Navnesen"
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-300">Stilling</Label>
                  <Input
                    value={profile?.contact_title || ""}
                    onChange={(e) => handleChange("contact_title", e.target.value)}
                    placeholder="Prosjektleder"
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> E-post *
                  </Label>
                  <Input
                    type="email"
                    value={profile?.email || ""}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="post@firma.no"
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-300 flex items-center gap-1">
                    <Phone className="w-3 h-3" /> Telefon
                  </Label>
                  <Input
                    value={profile?.phone || ""}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    placeholder="+47 123 45 678"
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </div>
              </div>
            </div>
            
            {/* Invoice Settings */}
            <div className="space-y-4">
              <h4 className="text-white font-medium flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#23F7DD]" />
                Fakturainnstillinger
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Faktura e-post</Label>
                  <Input
                    type="email"
                    value={profile?.invoice_email || ""}
                    onChange={(e) => handleChange("invoice_email", e.target.value)}
                    placeholder="faktura@firma.no"
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-300">Betalingsfrist (dager)</Label>
                  <Input
                    type="number"
                    min="7"
                    max="60"
                    value={profile?.payment_terms_days || 14}
                    onChange={(e) => handleChange("payment_terms_days", parseInt(e.target.value) || 14)}
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </div>
              </div>
            </div>
            
            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-[#334155]">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#23F7DD] text-black hover:bg-[#1ACBB8] gap-2"
              >
                <Save className="w-4 h-4" />
                {saving ? "Lagrer..." : "Lagre endringer"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
